import type {
  TGeofenceEvent,
  TGeofenceEventsPage,
  TGeofenceStationId,
  TGeofenceVisit,
  TGetGeofenceEvents,
  TGetGeofenceEventsQuery,
  TGetGeofenceFunnel,
  TGetGeofenceJourneys,
  TGetGeofenceSummary,
  TGetGeofenceTimeBucket,
  TGetGeofenceVisits,
  TGetGeofenceVisitsQuery,
  TGetLocationPings,
} from '@/types/geofence';

import { geofencePartnerService } from '@/api/private';

import { toStationIdFilterParam } from './stationFilterParam';

const PAGE_SIZE = 100;

const toStationIdList = (stationId?: TGeofenceStationId): string[] => {
  if (!stationId) {
    return [];
  }

  return (Array.isArray(stationId) ? stationId : [stationId]).filter(Boolean);
};

const withGeofenceStationFilter = <T extends { stationId?: TGeofenceStationId }>({
  stationId,
  ...params
}: T) => {
  return {
    ...params,
    ...toStationIdFilterParam(toStationIdList(stationId)),
  };
};

type TGeofencePaged<TItem> = {
  content?: TItem[];
  result?: TItem[];
  totalElements?: number;
  totalPages?: number;
  totalHits?: number;
  number?: number;
  numberOfElements?: number;
  last?: boolean;
  hasMore?: boolean;
};

export const getGeofenceSummary: TGetGeofenceSummary = (
  { minDwellSeconds = 60, ...params },
  signal,
) =>
  geofencePartnerService.get('/summary', {
    params: withGeofenceStationFilter({ ...params, minDwellSeconds }),
    signal,
  });

export const getGeofenceFunnel: TGetGeofenceFunnel = (
  { minDwellSeconds = 60, ...params },
  signal,
) =>
  geofencePartnerService.get('/funnel', {
    params: withGeofenceStationFilter({ ...params, minDwellSeconds }),
    signal,
  });

export const getGeofenceTimeBucket: TGetGeofenceTimeBucket = (
  { minDwellSeconds = 60, ...params },
  signal,
) =>
  geofencePartnerService.get('/time-bucket', {
    params: withGeofenceStationFilter({ ...params, minDwellSeconds }),
    signal,
  });

/**
 * Visits list (paged). Uses the partner geofence endpoint so one call covers every
 * selected site: station ids go in `filter` as `{"stationId":[...]}`, same as the
 * Visits Overview calls. A single id is sent as a one-item list.
 */
/**
 * Top customer journeys. `topPaths` and `dedupePathAmenities` belong in the filter
 * body with the station ids. Dates and `minDwellSeconds` stay query params, same as
 * the other geofence calls.
 */
export const getGeofenceJourneys: TGetGeofenceJourneys = (
  { stationId, from, to, topPaths = 4, dedupePathAmenities = false, minDwellSeconds = 60 },
  signal,
) => {
  const ids = toStationIdList(stationId);

  return geofencePartnerService.get('/journeys', {
    params: {
      from,
      to,
      minDwellSeconds,
      filter: JSON.stringify({
        ...(ids.length ? { stationId: ids } : {}),
        topPaths,
        dedupePathAmenities,
      }),
    },
    signal,
  });
};

/**
 * Paged visits. `sort` is `key,dir` (startedAt, endedAt, dwellSeconds, lastSeenAt,
 * stationId, status, transactionAmount). Omitted, the server uses startedAt,desc.
 */
export const getGeofenceVisits: TGetGeofenceVisits = (
  { minDwellSeconds = 60, sortBy, sortDir, ...params },
  signal,
) =>
  geofencePartnerService.get('/visits', {
    params: withGeofenceStationFilter({
      ...params,
      minDwellSeconds,
      ...(sortBy ? { sort: `${sortBy},${sortDir ?? 'desc'}` } : {}),
    }),
    signal,
  });

const extractPageItems = <TItem>(page?: TGeofencePaged<TItem>): TItem[] => {
  if (!page) {
    return [];
  }

  if (page.totalElements === 0 || page.numberOfElements === 0) {
    return [];
  }

  const items = page.content ?? page.result;

  return Array.isArray(items) ? items.filter((item): item is TItem => Boolean(item)) : [];
};

const isLastGeofencePage = (
  page: TGeofencePaged<unknown> | undefined,
  loadedCount: number,
): boolean => {
  if (!page) {
    return true;
  }

  if (page.last === true || page.hasMore === false) {
    return true;
  }

  if (page.totalElements === 0 || page.numberOfElements === 0) {
    return true;
  }

  if (page.totalPages != null) {
    if (page.totalPages === 0) {
      return true;
    }

    return (page.number ?? 0) >= page.totalPages - 1;
  }

  return loadedCount < PAGE_SIZE;
};

const remainingGeofencePages = (page: TGeofencePaged<unknown>, loadedCount: number): number => {
  if (page.totalPages != null && page.totalPages > 1) {
    return page.totalPages - 1;
  }

  const total = page.totalElements ?? page.totalHits;
  if (total != null && total > loadedCount) {
    return Math.ceil(total / PAGE_SIZE) - 1;
  }

  return 0;
};

const fetchAllGeofencePages = async <TItem>(
  fetchPage: (page: number) => Promise<{ data: { data: TGeofencePaged<TItem> } }>,
): Promise<TItem[]> => {
  const firstPayload = (await fetchPage(0)).data.data;
  const all = extractPageItems(firstPayload);

  if (isLastGeofencePage(firstPayload, all.length) || all.length === 0) {
    return all;
  }

  const remainingPages = remainingGeofencePages(firstPayload, all.length);

  if (remainingPages > 0) {
    const rest = await Promise.all(
      Array.from({ length: remainingPages }, (_, index) => fetchPage(index + 1)),
    );
    rest.forEach((response) => {
      all.push(...extractPageItems(response.data.data));
    });
    return all;
  }

  let page = 1;
  let hasMore = true;
  while (hasMore) {
    const payload = (await fetchPage(page)).data.data;
    const items = extractPageItems(payload);
    all.push(...items);
    hasMore = !isLastGeofencePage(payload, items.length) && items.length > 0;
    page += 1;
  }

  return all;
};

/**
 * Every visit of the range, page after page. The optional `signal` goes to every page call, so
 * cancelling stops the whole fan-out.
 */
export const fetchAllGeofenceVisits = async (
  params: Omit<TGetGeofenceVisitsQuery, 'page' | 'size'>,
  signal?: AbortSignal,
): Promise<TGeofenceVisit[]> =>
  fetchAllGeofencePages((page) =>
    getGeofenceVisits(
      {
        ...params,
        page,
        size: PAGE_SIZE,
      },
      signal,
    ),
  );

export const getGeofenceEvents: TGetGeofenceEvents = (
  { minDwellSeconds = 60, ...params },
  signal,
) =>
  geofencePartnerService.get('/events', {
    params: withGeofenceStationFilter({ ...params, minDwellSeconds }),
    signal,
  });

/**
 * Maps partner geofence events page payload into a flat events list.
 * Empty success responses may omit `content` entirely, e.g.:
 * { totalPages: 0, totalElements: 0, numberOfElements: 0, first: true, last: true }
 */
export const extractEvents = (page?: TGeofenceEventsPage): TGeofenceEvent[] =>
  extractPageItems(page);

/**
 * Every event of the range, page after page. The optional `signal` goes to every page call, so
 * cancelling stops the whole fan-out.
 */
export const fetchAllGeofenceEvents = async (
  params: Omit<TGetGeofenceEventsQuery, 'page' | 'size'>,
  signal?: AbortSignal,
): Promise<TGeofenceEvent[]> =>
  fetchAllGeofencePages((page) =>
    getGeofenceEvents(
      {
        ...params,
        page,
        size: PAGE_SIZE,
      },
      signal,
    ),
  );

export const getLocationPings: TGetLocationPings = (params, signal) =>
  geofencePartnerService.get('/location-pings', {
    params: withGeofenceStationFilter(params),
    signal,
  });

// The pings endpoint caps its page size at 100 regardless of the requested
// `size`, so we request 100 to match what it actually returns.
const PINGS_PAGE_SIZE = 100;

export type TFetchAllLocationPingsResult = {
  pings: import('@/types/geofence').TLocationPing[];
  totalPings: number;
  uniqueUsers: number;
  capped: boolean;
};

/**
 * Every ping of the range, page after page. The optional `signal` goes to every page call, so
 * cancelling stops the whole fan-out.
 */
export const fetchAllLocationPings = async (
  stationId: TGeofenceStationId,
  params: { from: string; to: string },
  signal?: AbortSignal,
): Promise<TFetchAllLocationPingsResult> => {
  const fetchPage = (page: number) =>
    getLocationPings(
      {
        stationId,
        from: params.from,
        to: params.to,
        includeDwellPings: true,
        page,
        size: PINGS_PAGE_SIZE,
      },
      signal,
    );

  // An empty answer (`{}`) has no `data`: no pings, so every field falls back to empty.
  const firstPayload = (await fetchPage(0)).data.data;
  const totalPings = firstPayload?.totalPings ?? 0;
  const uniqueUsers = firstPayload?.uniqueUsers ?? 0;
  const all: import('@/types/geofence').TLocationPing[] = [...(firstPayload?.pings?.content ?? [])];

  const totalPages = firstPayload?.pings?.totalPages ?? 1;
  const remainingPages = firstPayload?.pings?.last || totalPages <= 1 ? 0 : totalPages - 1;

  if (remainingPages > 0) {
    const rest = await Promise.all(
      Array.from({ length: remainingPages }, (_, index) => fetchPage(index + 1)),
    );
    rest.forEach((response) => {
      all.push(...(response.data.data?.pings?.content ?? []));
    });
  }

  return { pings: all, totalPings, uniqueUsers, capped: false };
};
